import { useQuery } from '@tanstack/react-query'
import httpClient from '~/modules/core/httpClient'
import { sortObjAlphabetically } from '../utility'
import { get as getItem } from '~/utils/storage/memoryStorage'
import { useIsAccessibleAllLocation } from '~/redux/hooks/user'
import { useDebugMode } from '~/utils/debugUtils'

interface IWhsData {
  cova_metadata: any
  greenline_mapping: any
  pos_mapping: any
  name: string
  _id: string
}
interface IOption {
  defaultAllValue: any
}

const fetchWhs = (store_id: string, showAllLocation = false) => {
  if (!showAllLocation) {
    return httpClient.json.get('organizations/stores/warehouses/protected', {
      params: {
        ...(getItem('ORGANIZATION_ID')
          ? { organization_id: getItem('ORGANIZATION_ID') }
          : {}),
        store_id,
      },
    })
  }
  return httpClient.json.get('organizations/stores/warehouses', {
    params: {
      ...(getItem('ORGANIZATION_ID')
        ? { organization_id: getItem('ORGANIZATION_ID') }
        : {}),
      store_id,
    },
  })
}

export const useWHLocations = (storeId: string, showAllLocation = false) => {
  return useQuery({
    queryKey: ['useOrgWarehouses_locations', storeId, showAllLocation],
    queryFn: () => fetchWhs(storeId, showAllLocation),
    initialData: [] as any,
    select: (data) =>
      (data || [])
        //@ts-ignore
        .sort((a: any, b: any) => sortObjAlphabetically(a, b, 'name'))
        .map((v: IWhsData) => ({
          company_id:
            v.cova_metadata?.CompanyId ||
            v.greenline_mapping?.company_id ||
            v.pos_mapping?.company_id,
          id:
            v.cova_metadata?.LocationId ||
            v.greenline_mapping?.location_id ||
            v.pos_mapping?.location_id,
          name: v.name,
          warehouse_id: v._id,
        })),
    enabled: !!storeId,
  })
}

export const useWHLocationOptions = (
  storeId: string,
  opts: any = { defaultAllValue: '' },
) => {
  const isAccessibleAllLocation = useIsAccessibleAllLocation()
  const initialOptions = isAccessibleAllLocation
    ? [
        {
          value: opts.defaultAllValue,
          label: 'All locations',
          name: 'All locations',
        },
      ]
    : []

  //@ts-ignore
  return useQuery({
    queryKey: ['useOrgWarehouses_options', storeId],
    queryFn: () => fetchWhs(storeId, false),
    initialData: [],
    select: (data) =>
      initialOptions.concat(
        (data || [])
          //@ts-ignore
          .sort((a, b) => sortObjAlphabetically(a, b, 'name'))
          .map((item) => ({
            value:
              item.cova_metadata?.LocationId ||
              item.greenline_mapping?.location_id ||
              item?.pos_mapping?.location_id, // Flowhub
            label: item.name,
          })),
      ),
  })
}

export const useWHOptions = (
  storeId: string,
  opts: any = { defaultAllValue: '', hasAllLocation: true },
) => {
  const isAccessibleAllLocation = useIsAccessibleAllLocation()
  const initialOptions =
    isAccessibleAllLocation && opts.hasAllLocation
      ? [
          {
            value: opts.defaultAllValue,
            label: 'All locations',
            name: 'All locations',
          },
        ]
      : []

  //@ts-ignore
  return useQuery({
    queryKey: ['useOrgWarehouses_options', storeId],
    queryFn: () => fetchWhs(storeId, false),
    initialData: [],
    select: (data) =>
      initialOptions.concat(
        (data || [])
          //@ts-ignore
          .sort((a, b) => sortObjAlphabetically(a, b, 'name'))
          .map((item) => ({
            value: item._id, // Flowhub
            label: item.name,
          })),
      ),
  })
}

export const useFullWHLocations = (storeId: string) => {
  return useQuery({
    queryKey: ['useOrgWarehouses_full', storeId],
    queryFn: () => fetchWhs(storeId, true),
    initialData: [] as any,
    select: (data) =>
      (data || [])
        //@ts-ignore
        .sort((a: any, b: any) => sortObjAlphabetically(a, b, 'name'))
        .map((v: IWhsData) => ({
          company_id:
            v.cova_metadata?.CompanyId ||
            v.greenline_mapping?.company_id ||
            v.pos_mapping?.company_id,
          id:
            v.cova_metadata?.LocationId ||
            v.greenline_mapping?.location_id ||
            v.pos_mapping?.location_id,
          name: v.name,
        })),
    enabled: !!storeId,
  })
}

export const useWHFullLocationOptions = (
  storeId: string,
  opts: IOption = { defaultAllValue: '' },
) => {
  const initialOptions = [
    {
      value: opts.defaultAllValue,
      label: 'All Locations',
      name: 'All Locations',
    },
  ]
  //@ts-ignore
  return useQuery({
    queryKey: ['useOrgWarehouses_full_options', storeId],
    queryFn: () => fetchWhs(storeId, true),
    initialData: [] as any,
    select: (data) =>
      initialOptions.concat(
        (data || [])
          //@ts-ignore
          .sort((a: any, b: any) => sortObjAlphabetically(a, b, 'name'))
          .map((item: IWhsData) => ({
            value:
              item.cova_metadata?.LocationId ||
              item.greenline_mapping?.location_id,
            label: item.name,
          })),
      ),
  })
}
